import { Fragment, useEffect, useState } from "react";
import type {
  ParentPlayerPublic,
  ChildTeamPublic,
  ChildConsentStatus,
  PolicyVersionPublic,
  EnrollmentRequestPublic,
} from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { AddChildDialog } from "@/components/add-child-dialog";
import { EditChildDialog } from "@/components/edit-child-dialog";
import { EnrollSportDialog } from "@/components/enroll-sport-dialog";
import { PlayerHealthSection } from "@/components/player-health-section";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Heart,
  Link2,
  Unlink,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  ShieldCheck,
  Loader2,
  MessageSquare,
  Plus,
  CheckCircle2,
  Pencil,
  HeartPulse,
} from "lucide-react";
import { SchoolCrest } from "@/components/school-crest";
import { AvailabilityRequestsCard } from "@/components/availability-requests-card";
import { MessageCoachDialog } from "@/components/message-coach-dialog";
import { useIsSmUp } from "@/lib/use-media-query";

export default function MyChildrenPage() {
  const smUp = useIsSmUp();
  const [children, setChildren] = useState<ParentPlayerPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Link form state
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [familyCode, setFamilyCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  // Expanded child (show teams)
  const [expandedChildId, setExpandedChildId] = useState<string | null>(null);
  // 140 -- "Message coach" dialog target.
  const [messageChild, setMessageChild] = useState<{ player_id: string; player_name: string } | null>(null);
  const [childTeams, setChildTeams] = useState<ChildTeamPublic[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // 143 -- POPIA guardian consent
  const [consentByChild, setConsentByChild] = useState<Map<string, ChildConsentStatus>>(new Map());
  const [childPolicy, setChildPolicy] = useState<PolicyVersionPublic | null>(null);
  const [consentingChild, setConsentingChild] = useState<ParentPlayerPublic | null>(null);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  // 189 -- self-serve: add-child / enrol-in-sport / per-sport indemnity + status
  const [enrollmentsByChild, setEnrollmentsByChild] = useState<Map<string, EnrollmentRequestPublic[]>>(new Map());
  const [addingChild, setAddingChild] = useState(false);
  const [editChild, setEditChild] = useState<ParentPlayerPublic | null>(null);
  const [healthChild, setHealthChild] = useState<ParentPlayerPublic | null>(null);
  const [enrollChild, setEnrollChild] = useState<ParentPlayerPublic | null>(null);
  const [indemnityTarget, setIndemnityTarget] = useState<{ child: ParentPlayerPublic; request: EnrollmentRequestPublic } | null>(null);
  const [indemnityUrl, setIndemnityUrl] = useState("");
  const [indemnitySaving, setIndemnitySaving] = useState(false);

  useEffect(() => {
    loadChildren();
  }, []);

  async function loadChildren() {
    setLoading(true);
    setError(null);
    try {
      const [res, consent] = await Promise.all([
        api.getMyChildren(),
        api.getChildrenConsentStatus(),
      ]);
      setChildren(res.children);
      setConsentByChild(new Map(consent.children.map((c) => [c.player_id, c])));
      setChildPolicy(consent.policy);
      await loadEnrollments(res.children.map((c) => c.player_id));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load children");
    } finally {
      setLoading(false);
    }
  }

  async function loadEnrollments(playerIds: string[]) {
    try {
      const lists = await Promise.all(
        playerIds.map((id) =>
          api.getChildEnrollmentRequests(id)
            .then((r) => [id, r.requests] as [string, EnrollmentRequestPublic[]])
            .catch(() => [id, [] as EnrollmentRequestPublic[]] as [string, EnrollmentRequestPublic[]]),
        ),
      );
      setEnrollmentsByChild(new Map(lists));
    } catch {
      // non-fatal -- signups area just shows empty
    }
  }

  async function reloadEnrollments(playerId: string) {
    try {
      const r = await api.getChildEnrollmentRequests(playerId);
      setEnrollmentsByChild((prev) => new Map(prev).set(playerId, r.requests));
    } catch {
      // non-fatal
    }
  }

  async function withdrawSignup(playerId: string, request: EnrollmentRequestPublic) {
    const approved = request.status === "APPROVED";
    const msg = approved
      ? `Leave ${request.sport_name}? Your child will be removed from the team. You can sign up again later.`
      : `Withdraw ${request.sport_name} signup? You can sign up again later.`;
    if (!confirm(msg)) return;
    try {
      await api.withdrawEnrollmentRequest(playerId, request.id);
      // Approved withdrawal un-rosters the child, so the team count changes too.
      if (approved) await loadChildren();
      else await reloadEnrollments(playerId);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to withdraw signup");
    }
  }

  function openIndemnity(child: ParentPlayerPublic, request: EnrollmentRequestPublic) {
    setIndemnityUrl(request.indemnity_document_url ?? "");
    setIndemnityTarget({ child, request });
  }

  async function submitIndemnity() {
    if (!indemnityTarget) return;
    setIndemnitySaving(true);
    try {
      await api.updateEnrollmentIndemnity(indemnityTarget.child.player_id, indemnityTarget.request.id, {
        indemnity_complete: true,
        indemnity_document_url: indemnityUrl.trim() || null,
      });
      await reloadEnrollments(indemnityTarget.child.player_id);
      setIndemnityTarget(null);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to update indemnity");
    } finally {
      setIndemnitySaving(false);
    }
  }

  async function loadConsentStatus() {
    try {
      const consent = await api.getChildrenConsentStatus();
      setConsentByChild(new Map(consent.children.map((c) => [c.player_id, c])));
      setChildPolicy(consent.policy);
    } catch {
      // non-fatal; leave existing status in place
    }
  }

  async function submitConsent() {
    if (!consentingChild || !childPolicy) return;
    setConsentSaving(true);
    setConsentError(null);
    try {
      await api.recordChildConsent(consentingChild.player_id, childPolicy.version);
      setConsentingChild(null);
      await loadConsentStatus();
    } catch (e) {
      setConsentError(e instanceof api.ApiError ? e.message : "Failed to record consent");
    } finally {
      setConsentSaving(false);
    }
  }

  async function withdrawConsent(playerId: string, playerName: string) {
    if (!confirm(`Withdraw consent for ${playerName}? They will lose access to their player account until you approve again.`)) return;
    try {
      await api.withdrawChildConsent(playerId);
      await loadConsentStatus();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to withdraw consent");
    }
  }

  async function handleLink() {
    if (!familyCode.trim()) return;
    setLinking(true);
    setLinkError(null);
    setLinkSuccess(null);
    try {
      const res = await api.linkChild(familyCode.trim());
      const names = res.children.map((c) => c.player_name).join(", ");
      setLinkSuccess(`Linked to: ${names}`);
      setFamilyCode("");
      loadChildren();
    } catch (e) {
      setLinkError(e instanceof api.ApiError ? e.message : "Failed to link");
    } finally {
      setLinking(false);
    }
  }

  async function handleUnlink(playerId: string, playerName: string) {
    if (!confirm(`Are you sure you want to unlink from ${playerName}?`)) return;
    try {
      await api.unlinkChild(playerId);
      setChildren((prev) => prev.filter((c) => c.player_id !== playerId));
      if (expandedChildId === playerId) {
        setExpandedChildId(null);
        setChildTeams([]);
      }
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to unlink");
    }
  }

  async function toggleTeams(playerId: string) {
    if (expandedChildId === playerId) {
      setExpandedChildId(null);
      setChildTeams([]);
      return;
    }

    setExpandedChildId(playerId);
    setTeamsLoading(true);
    try {
      const res = await api.getChildTeams(playerId);
      setChildTeams(res.teams);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load teams");
    } finally {
      setTeamsLoading(false);
    }
  }

  // 185: shared cell renderers so the desktop table and the mobile cards stay a
  // single source (the consent cell + teams block + actions are non-trivial).
  function consentState(child: ParentPlayerPublic): string {
    const cs = consentByChild.get(child.player_id);
    return cs?.consented ? "consented" : cs?.consent_required ? "required" : "na";
  }

  function consentCell(child: ParentPlayerPublic) {
    const cs = consentByChild.get(child.player_id);
    if (!cs || !cs.is_minor) {
      return <span className="text-xs text-muted-foreground">Not required (18+)</span>;
    }
    if (!cs.consent_required) {
      // 204 -- "Awaiting policy" read as an action the parent owed; it means no
      // child-data policy is published yet, so consent is not yet required.
      return (
        <span className="text-xs text-muted-foreground" title="Consent becomes required once a child-data policy is published">
          Not required yet
        </span>
      );
    }
    if (cs.consented) {
      return (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Approved
          </span>
          <Button variant="ghost" size="sm" className="h-6 px-1 text-xs text-muted-foreground" onClick={() => withdrawConsent(child.player_id, child.player_name)} data-action="withdraw-consent">
            Withdraw
          </Button>
        </div>
      );
    }
    return (
      <Button size="sm" className="h-7 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" variant="outline" onClick={() => { setConsentError(null); setConsentingChild(child); }} data-action="approve-consent">
        <ShieldAlert className="mr-1 h-3.5 w-3.5" /> Approve
      </Button>
    );
  }

  function actionButtons(child: ParentPlayerPublic) {
    return (
      <>
        <Button variant="ghost" size="sm" onClick={() => setEditChild(child)} data-action="edit-child" title="Edit details">
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setHealthChild(child)} data-action="edit-health" title="Health & emergency">
          <HeartPulse className="h-4 w-4 text-muted-foreground" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setMessageChild({ player_id: child.player_id, player_name: child.player_name })} data-action="message-coach" title="Message coach">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleUnlink(child.player_id, child.player_name)} data-action="unlink">
          <Unlink className="h-4 w-4 text-destructive" />
        </Button>
      </>
    );
  }

  function teamsBlock() {
    if (teamsLoading) {
      return <p className="text-sm text-muted-foreground py-2">Loading teams...</p>;
    }
    if (childTeams.length === 0) {
      return <p className="text-sm text-muted-foreground py-2">Not assigned to any teams yet</p>;
    }
    return (
      <div className="py-2">
        <p className="text-sm font-medium mb-2">Teams</p>
        <div className="flex flex-col gap-1">
          {childTeams.map((team) => (
            <div key={team.id} className="flex flex-wrap items-center gap-3 text-sm rounded-md bg-muted/50 px-3 py-1.5">
              <SchoolCrest name={team.school_name} logoUrl={team.school_logo_url} primaryColor={team.school_primary_color} size={20} />
              <span className="font-medium">{team.name}</span>
              <span className="text-muted-foreground">{team.age_group}</span>
              <span className="text-muted-foreground">{team.player_count} players</span>
              <span className="text-muted-foreground">{team.coach_count} coaches</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function statusBadge(r: EnrollmentRequestPublic) {
    if (r.status === "APPROVED") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
          <ShieldCheck className="h-3.5 w-3.5" /> Approved{r.assigned_team_name ? ` - ${r.assigned_team_name}` : ""}
        </span>
      );
    }
    if (r.status === "DECLINED") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700" title={r.decline_reason ?? undefined}>
          Declined{r.decline_reason ? `: ${r.decline_reason}` : ""}
        </span>
      );
    }
    if (r.status === "WITHDRAWN") {
      return <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">Withdrawn</span>;
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
        Pending approval
      </span>
    );
  }

  function signupsBlock(child: ParentPlayerPublic) {
    const reqs = enrollmentsByChild.get(child.player_id) ?? [];
    return (
      <div className="py-2" data-region="signups">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium">Sport signups</p>
          <Button size="sm" variant="outline" onClick={() => setEnrollChild(child)} data-action="enrol-sport">
            <Plus className="mr-1 h-3.5 w-3.5" /> Enrol in a sport
          </Button>
        </div>
        {reqs.length === 0 ? (
          <p className="py-1 text-sm text-muted-foreground">No sport signups yet</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {reqs.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/50 px-3 py-1.5 text-sm" data-item="signup" data-item-id={r.id} data-status={r.status}>
                <span className="font-medium">{r.sport_name}</span>
                {statusBadge(r)}
                {(r.status === "PENDING" || r.status === "APPROVED") && (
                  r.indemnity_complete ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-region="indemnity" data-complete="true">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Indemnity complete
                    </span>
                  ) : (
                    <Button variant="outline" size="sm" className="h-6 border-amber-300 bg-amber-50 px-2 text-xs text-amber-800 hover:bg-amber-100" onClick={() => openIndemnity(child, r)} data-action="complete-indemnity">
                      Complete indemnity
                    </Button>
                  )
                )}
                {(r.status === "PENDING" || r.status === "APPROVED") && (
                  <Button variant="ghost" size="sm" className="h-6 px-1 text-xs text-muted-foreground" onClick={() => withdrawSignup(child.player_id, r)} data-action="withdraw-signup">
                    {r.status === "APPROVED" ? "Leave sport" : "Withdraw"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-page="my-children" data-state={loading ? "loading" : error ? "error" : children.length === 0 ? "empty" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">My Children</h1>
          <p className="text-muted-foreground">
            Link to your children using their family code
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAddingChild(true)} data-action="add-child">
            <Plus className="mr-2 h-4 w-4" />
            Add Child
          </Button>
          <Button size="sm" onClick={() => { setShowLinkForm(true); setLinkError(null); setLinkSuccess(null); }} data-action="link-child">
            <Link2 className="mr-2 h-4 w-4" />
            Link Child
          </Button>
        </div>
      </div>

      {/* 138 -- pending availability requests for the parent's children */}
      <AvailabilityRequestsCard />

      {(() => {
        const pending = [...consentByChild.values()].filter((c) => c.consent_required && !c.consented).length;
        if (pending === 0) return null;
        return (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-region="consent-banner">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">
                {pending === 1 ? "1 child needs your approval" : `${pending} children need your approval`}
              </p>
              <p className="text-amber-800">
                Your child cannot use their player account until you approve how we process their
                personal information. Use the <span className="font-medium">Approve</span> button next to each child.
              </p>
            </div>
          </div>
        );
      })()}

      {showLinkForm && (
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="text-base">Link Child</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              {linkError && <p className="text-sm text-destructive">{linkError}</p>}
              {linkSuccess && <p className="text-sm text-green-600">{linkSuccess}</p>}
              <div className="flex flex-col gap-2">
                <Label htmlFor="family-code">Family Code</Label>
                <Input
                  id="family-code"
                  placeholder="Enter family code (e.g. TESTFAM1)"
                  value={familyCode}
                  onChange={(e) => setFamilyCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleLink();
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the family code provided by your child's school. This
                  links your account to your child's profile.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleLink}
                  disabled={linking || !familyCode.trim()}
                >
                  {linking ? "Linking..." : "Link"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowLinkForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {!loading && !error && children.length === 0 && !showLinkForm && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <Heart className="h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">No children linked yet</p>
          <p className="text-sm text-muted-foreground">
            Use a family code from your child's school to link your account
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLinkForm(true)}
          >
            Link your first child
          </Button>
        </div>
      )}

      {!loading && children.length > 0 && (
        <div data-region="results">
          {smUp ? (
          /* Desktop table */
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Name</TableHead>
                  <TableHead>School</TableHead>
                  <TableHead>Date of Birth</TableHead>
                  <TableHead>Teams</TableHead>
                  <TableHead>Linked</TableHead>
                  <TableHead>Approval</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {children.map((child) => (
                  <Fragment key={child.player_id}>
                    <TableRow data-item="child" data-item-id={child.player_id}>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => toggleTeams(child.player_id)}>
                          {expandedChildId === child.player_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{child.player_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <SchoolCrest name={child.school_name} logoUrl={child.school_logo_url} primaryColor={child.school_primary_color} size={24} />
                          <span>{child.school_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{child.date_of_birth}</TableCell>
                      <TableCell className="text-muted-foreground">{child.team_count}</TableCell>
                      <TableCell className="text-muted-foreground">{new Date(child.linked_at).toLocaleDateString()}</TableCell>
                      <TableCell data-region="consent" data-consent-state={consentState(child)}>{consentCell(child)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">{actionButtons(child)}</div>
                      </TableCell>
                    </TableRow>
                    {expandedChildId === child.player_id && (
                      <TableRow>
                        <TableCell />
                        <TableCell colSpan={7}>
                          {signupsBlock(child)}
                          {teamsBlock()}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
          ) : (
          /* Mobile cards (below sm) */
          <div className="flex flex-col gap-2">
            {children.map((child) => (
              <div key={child.player_id} className="rounded-lg border border-border bg-card p-3" data-item="child" data-item-id={child.player_id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 font-medium">{child.player_name}</div>
                  <div className="flex shrink-0 items-center">{actionButtons(child)}</div>
                </div>
                <dl className="mt-2 grid grid-cols-[minmax(0,auto)_1fr] gap-x-3 gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">School</dt>
                  <dd className="min-w-0">
                    <div className="flex items-center justify-end gap-2">
                      <SchoolCrest name={child.school_name} logoUrl={child.school_logo_url} primaryColor={child.school_primary_color} size={20} />
                      <span>{child.school_name}</span>
                    </div>
                  </dd>
                  <dt className="text-muted-foreground">Date of Birth</dt>
                  <dd className="text-right">{child.date_of_birth}</dd>
                  <dt className="text-muted-foreground">Linked</dt>
                  <dd className="text-right">{new Date(child.linked_at).toLocaleDateString()}</dd>
                  <dt className="text-muted-foreground">Approval</dt>
                  <dd className="text-right" data-region="consent" data-consent-state={consentState(child)}>{consentCell(child)}</dd>
                </dl>
                <div className="mt-2 border-t border-border pt-2">
                  <button type="button" className="flex items-center gap-1 text-sm text-muted-foreground" onClick={() => toggleTeams(child.player_id)} data-action="toggle-teams">
                    {expandedChildId === child.player_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {child.team_count} {child.team_count === 1 ? "team" : "teams"}
                  </button>
                  {expandedChildId === child.player_id && (
                    <>
                      {signupsBlock(child)}
                      {teamsBlock()}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {consentingChild && childPolicy && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/60 p-4" data-dialog="child-consent">
          <Card className="mt-12 w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Approve data processing for {consentingChild.player_name}</CardTitle>
              <p className="text-xs text-muted-foreground">
                Version {childPolicy.version} - as the parent/guardian, you are consenting on behalf of your child.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <article className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded border bg-muted/20 p-3 text-sm" data-region="policy-body">
                {childPolicy.body_markdown}
              </article>
              {consentError && <p className="text-sm text-destructive" data-error>{consentError}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConsentingChild(null)} disabled={consentSaving}>
                  Cancel
                </Button>
                <Button onClick={submitConsent} disabled={consentSaving} data-action="confirm-consent">
                  {consentSaving ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Recording...
                    </>
                  ) : (
                    "I consent on behalf of my child"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {messageChild && (
        <MessageCoachDialog
          playerId={messageChild.player_id}
          playerName={messageChild.player_name}
          onClose={() => setMessageChild(null)}
        />
      )}

      {addingChild && (
        <AddChildDialog
          onClose={() => setAddingChild(false)}
          onCreated={async () => {
            setAddingChild(false);
            await loadChildren();
          }}
        />
      )}

      {editChild && (
        <EditChildDialog
          child={editChild}
          onClose={() => setEditChild(null)}
          onSaved={async () => {
            setEditChild(null);
            await loadChildren();
          }}
        />
      )}

      {healthChild && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
          data-dialog="child-health"
        >
          <div className="w-full max-w-lg">
            <PlayerHealthSection playerId={healthChild.player_id} />
            <div className="mt-2 flex justify-end">
              <Button variant="outline" onClick={() => setHealthChild(null)} data-action="close-health">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {enrollChild && (
        <EnrollSportDialog
          playerId={enrollChild.player_id}
          playerName={enrollChild.player_name}
          schoolId={enrollChild.school_id}
          schoolName={enrollChild.school_name}
          onClose={() => setEnrollChild(null)}
          onSubmitted={async () => {
            const pid = enrollChild.player_id;
            setEnrollChild(null);
            setExpandedChildId(pid);
            await reloadEnrollments(pid);
          }}
        />
      )}

      {indemnityTarget && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center" data-dialog="indemnity">
          <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
            <CardHeader>
              <CardTitle>Indemnity - {indemnityTarget.request.sport_name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Confirm the indemnity form for {indemnityTarget.child.player_name} is complete. You can
                optionally paste a link to the signed document.
              </p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="indemnity-url">Document link (optional)</Label>
                <Input
                  id="indemnity-url"
                  placeholder="https://..."
                  value={indemnityUrl}
                  onChange={(e) => setIndemnityUrl(e.target.value)}
                  data-field="indemnity-url"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIndemnityTarget(null)} disabled={indemnitySaving}>
                  Cancel
                </Button>
                <Button onClick={submitIndemnity} disabled={indemnitySaving} data-action="confirm-indemnity">
                  {indemnitySaving ? "Saving..." : "Mark complete"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
